import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    TextField,
    Alert,
    CircularProgress,
    Divider,
    ToggleButton,
    ToggleButtonGroup,
    Stepper,
    Step,
    StepLabel,
} from "@mui/material";
import {
    Close as CloseIcon,
    LocalHospital as LocalHospitalIcon,
    VideoCall as VideoCallIcon,
    EventAvailable as EventAvailableIcon,
    Save as SaveIcon,
    ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import { toast } from "react-toastify";
import axios from "axios"; // used for EMR backend calls (get_patient_history, diabetic profile)
// htlifeAxios is the dedicated instance for HTLife service calls.
// It auto-attaches Authorization + Org-Code + App-Mode and handles token refresh on 401.
import htlifeAxios, { getValidToken } from "../../utils/htlifeAxios";
import HistoryTab from "../../pages/appointments/HistoryTab";
import { initalMedicalBackgroundData } from "../../pages/doctor-console/initialData/initialData";
import "./AppointmentBookingModal.scss";

const STEPS = ["Book Appointment", "Medical History", "Done"];


const AppointmentBookingModal = ({
    open,
    onClose,
    doctor,
    slot,
    job_id,
    patient,
    orgCode,
    appMode,
    authToken,
    onGoToMyAppointments,
}) => {
    const [step, setStep] = useState(0);
    const [appointmentMode, setAppointmentMode] = useState("OFFLINE");
    const [notes, setNotes] = useState("");
    const [isBooking, setIsBooking] = useState(false);
    const [bookingDetails, setBookingDetails] = useState(null);
    const [isSavingHistory, setIsSavingHistory] = useState(false);
    const [medicalBackground, setMedicalBackground] = useState(initalMedicalBackgroundData);

    const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
    const REACT_APP_BACKEND_ROUTE_URL_HTLIFE = process.env.REACT_APP_BACKEND_ROUTE_URL_HTLIFE;
    const { patientId, patientUsername, patientPassword } = patient || {};

    const saveDiabeticProfile = async (updatedBackground) => {
        if (!patientId) return;
        const resolvedOrgCode = orgCode || sessionStorage.getItem("ORG_CODE") || "";
        const resolvedAppMode = appMode || sessionStorage.getItem("APP_MODE") || "";
        const authHeaders = {
            "Org-Code": resolvedOrgCode,
            "App-Mode": resolvedAppMode,
            "Username": sessionStorage.getItem("userName") || "",
            "User-Access-Key": sessionStorage.getItem("USER_ACCESS_KEY") || "",
        };
        if (authToken) authHeaders["Authorization"] = `Bearer ${authToken}`;

        try {
            await axios.post(
                `${REACT_APP_BACKEND_ROUTE_URL}create_patient_diabetic_profile/?patient_id=${encodeURIComponent(patientId)}`,
                {
                    patient_id: patientId,
                    chief_complaints: updatedBackground["History of Present Illness"]?.new || [],
                    medical_history: updatedBackground["medical_history"]?.new || [],
                    history_of_present_illness: updatedBackground["History of Present Illness"]?.new || [],
                    family_history: updatedBackground["Family History"]?.new || [],
                    drug_history: updatedBackground["Drug History"]?.new || [],
                    allergies: updatedBackground["Allergies"]?.new || [],
                    addiction_history: updatedBackground["Addiction History"]?.new || [],
                    menstrual_history: updatedBackground["Menstrual History"]?.new || [],
                    surgeries_history: updatedBackground["Surgeries"]?.new || [],
                    vaccination_history: updatedBackground["Adult Vaccination"]?.new || [],
                    current_treatment: "",
                    absent_pedal_pulses: {},
                    active_ulcer: {},
                    advice: "",
                    blisters: {},
                    callus: {},
                    cardiovascular_risk: "",
                    cbc: "",
                    ckd: "",
                    corn: {},
                    deformities_charcot_foot: {},
                    deformities_claw_toes: {},
                    deformities_flat_foot: {},
                    deformities_hallux_valgus: {},
                    deformities_hammer_toes: {},
                    dental_normal: false,
                    diabetes_onset_from: "",
                    diabetes_onset_time: "",
                    diabetes_type: "",
                    diabetic_foot: "",
                    diabetic_retinopathy: "",
                    diet_plan: "",
                    exercise_plan: "",
                    fasting_blood_sugar: "",
                    fib_4: "",
                    fissures: {},
                    foot_examination: "",
                    foot_normal: false,
                    gums_status: "",
                    hba1c: "",
                    hypertension: "",
                    hypertension_onset_time: "",
                    hypoglycemia: "",
                    infection_pus: {},
                    infection_redness: {},
                    infection_swelling: {},
                    kidney_function_tests: "",
                    left_foot_risk: "",
                    lifestyle_modifications: "",
                    lipid_disorders: "",
                    lipid_profile: "",
                    liver_function_tests: "",
                    metabolic_syndrome: "",
                    nails_fungal: {},
                    nails_ingrown: {},
                    nails_thickened: {},
                    nephropathy: "",
                    neuropathy: {},
                    other_ailments: "",
                    other_investigations: "",
                    patient_motivation: "",
                    peripheral_neuropathy: "",
                    peripheral_vascular_disease: "",
                    post_prandial_blood_sugar: "",
                    previous_amputation: {},
                    previous_treatment: "",
                    previous_ulcer: {},
                    primary_diagnosis: "",
                    quantitative_avg_vpt: { left: "", right: "" },
                    right_foot_risk: "",
                    secondary_diagnosis: "",
                    skin_hydration: "",
                    skin_normal: false,
                    skin_stretchability: "",
                    skin_texture: "",
                    teeth_status: "",
                    thyroid_profile: "",
                    treatment_medications: "",
                    ucar: "",
                },
                { headers: authHeaders }
            );
        } catch (_) {
            // Non-blocking
        }
    };

    const updateMedicalBackground = (type, data) => {
        setMedicalBackground((prev) => ({ ...prev, [type]: data }));
    };

    const handleModeChange = (_, newMode) => {
        if (newMode) setAppointmentMode(newMode);
    };

    // ── Step 0: Book Appointment ──────────────────────────────────────────────
    const handleBookAppointment = async () => {
        setIsBooking(true);

        // Proactively ensure a valid token exists before making any htlife calls.
        // This avoids the 401 → refresh → retry round-trip and catches session
        // expiry early with a clear user-facing message.
        const validToken = await getValidToken();
        if (!validToken) {
            // getValidToken() already dispatched "htlife-session-expired" which
            // will redirect the user to login — just stop here.
            setIsBooking(false);
            return;
        }

        const roomId = `hlthtek-${patientId || patientUsername || Date.now()}`;

        try {
            const slotDate = doctor.next_available_date;
            const startTime = convertToDateTime(slotDate, slot.slot_start_time);
            const endTime = convertToDateTime(slotDate, slot.slot_end_time);

            // htlifeAxios handles auth headers + token refresh automatically.
            await htlifeAxios.post("book_patient_appointment/", {
                job_id,
                doctor_id: doctor.doctor_id,
                appointment_start_time: startTime,
                appointment_end_time: endTime,
                appointment_mode: appointmentMode,
                status: "SCHEDULED",
                shift: slot.shiftNumber,
                slot_number: slot.slot_number,
                username: patientUsername || "",
                password: patientPassword || "",
                notes,
            });
        } catch (_) {
            // Non-blocking — appointment booking failure should not block the UI flow.
        }

        setIsBooking(false);
        setBookingDetails({
            date: doctor.next_available_date,
            startTime: slot.slot_start_time,
            endTime: slot.slot_end_time,
            doctorName: doctor.salutation
                ? `${doctor.salutation} ${doctor.name}`
                : doctor.name,
            mode: appointmentMode,
            roomId,
            telemedicineLink: `${window.location.origin}/patient/tele-console-patient?room=${encodeURIComponent(roomId)}&name=${encodeURIComponent(patientUsername || "Patient")}`,
        });
        setStep(1);
    };

    // ── Fetch existing patient history to prefill Step 1 ─────────────────────
    const fetchPatientHistory = async () => {
        const resolvedOrgCode = orgCode || sessionStorage.getItem("ORG_CODE") || "";
        const resolvedAppMode = appMode || sessionStorage.getItem("APP_MODE") || "";
        const resolvedAccessKey = sessionStorage.getItem("USER_ACCESS_KEY") || "";
        const reqHeaders = {
            "Org-Code": resolvedOrgCode,
            "App-Mode": resolvedAppMode,
            "User-Access-Key": resolvedAccessKey,
        };
        if (authToken) reqHeaders["Authorization"] = `Bearer ${authToken}`;

        const historyBody = patientId
            ? { patient_id: patientId }
            : { patient_username: patientUsername, patient_password: patientPassword };

        const historyPromise = axios.post(
            `${REACT_APP_BACKEND_ROUTE_URL}get_patient_history/`,
            historyBody,
            { headers: reqHeaders }
        );

        const diabeticPromise = patientId
            ? axios.get(
                `${REACT_APP_BACKEND_ROUTE_URL}create_patient_diabetic_profile/?patient_id=${encodeURIComponent(patientId)}`,
                { headers: reqHeaders }
            )
            : Promise.resolve(null);

        const [historyResult, diabeticResult] = await Promise.allSettled([historyPromise, diabeticPromise]);

        try {
            let finalFamilyHistory = [],
                finalAllergyHistory = [],
                finalAddictionHistory = [],
                finalDrugHistory = [],
                finalMenstrualHistory = [],
                finalMedicalHistory = [],
                finalSurgicalHistory = [],
                finalImmunizationHistory = [];

            if (historyResult.status === "fulfilled" && historyResult.value?.data?.status === "Success") {
                const histories = historyResult.value.data.histories || [];
                histories.forEach((entry) => {
                    if (entry.medical_history) finalMedicalHistory = finalMedicalHistory.concat(entry.medical_history);
                    if (entry.surgical_history) finalSurgicalHistory = finalSurgicalHistory.concat(entry.surgical_history);
                    if (entry.family_history) finalFamilyHistory = finalFamilyHistory.concat(entry.family_history);
                    if (entry.allergy_history) finalAllergyHistory = finalAllergyHistory.concat(entry.allergy_history);
                    if (entry.addiction_history) finalAddictionHistory = finalAddictionHistory.concat(entry.addiction_history);
                    if (entry.drug_history) finalDrugHistory = finalDrugHistory.concat(entry.drug_history);
                    if (entry.menstrual_history) finalMenstrualHistory = finalMenstrualHistory.concat(entry.menstrual_history);
                    if (entry.immunization_history) finalImmunizationHistory = finalImmunizationHistory.concat(entry.immunization_history);
                });
            }

            let diabeticHistory = {};
            if (diabeticResult?.status === "fulfilled" && diabeticResult.value) {
                const pd = diabeticResult.value.data;
                diabeticHistory = (Array.isArray(pd?.data) ? pd.data[0] : (Array.isArray(pd?.case_sheet) ? pd.case_sheet[0] : pd)) || {};
            }

            const getDiabeticField = (field) =>
                diabeticHistory[field] && Array.isArray(diabeticHistory[field]) ? diabeticHistory[field] : [];

            const hasPrev =
                finalMedicalHistory.length > 0 || finalSurgicalHistory.length > 0 ||
                finalFamilyHistory.length > 0 || finalAllergyHistory.length > 0 ||
                finalAddictionHistory.length > 0 || finalDrugHistory.length > 0 ||
                finalMenstrualHistory.length > 0 || finalImmunizationHistory.length > 0;

            const hasDiabetic =
                getDiabeticField("family_history").length > 0 || getDiabeticField("allergies").length > 0 ||
                getDiabeticField("addiction_history").length > 0 || getDiabeticField("drug_history").length > 0 ||
                getDiabeticField("menstrual_history").length > 0 || getDiabeticField("medical_history").length > 0 ||
                getDiabeticField("surgeries_history").length > 0 || getDiabeticField("vaccination_history").length > 0;

            if (hasPrev || hasDiabetic) {
                setMedicalBackground((prev) => ({
                    ...prev,
                    "medical_history": { prev: finalMedicalHistory, new: getDiabeticField("medical_history") },
                    "History of Present Illness": { prev: [], new: getDiabeticField("history_of_present_illness") },
                    "Family History": { prev: finalFamilyHistory, new: getDiabeticField("family_history") },
                    "Drug History": { prev: finalDrugHistory, new: getDiabeticField("drug_history") },
                    Allergies: { prev: finalAllergyHistory, new: getDiabeticField("allergies") },
                    "Addiction History": { prev: finalAddictionHistory, new: getDiabeticField("addiction_history") },
                    "Menstrual History": { prev: finalMenstrualHistory, new: getDiabeticField("menstrual_history") },
                    Surgeries: { prev: finalSurgicalHistory, new: getDiabeticField("surgeries_history") },
                    "Adult Vaccination": { prev: finalImmunizationHistory, new: getDiabeticField("vaccination_history") },
                }));
            }
        } catch (_) {
            // Non-blocking
        }
    };

    // ── Step 1: Save History ──────────────────────────────────────────────────
    const handleSaveHistory = async () => {
        setIsSavingHistory(true);

        const validToken = await getValidToken();
        if (!validToken) {
            setIsSavingHistory(false);
            return;
        }

        const historyPayload = {
            medical_history: medicalBackground["medical_history"]?.new || [],
            history_of_present_illness: medicalBackground["History of Present Illness"]?.new || [],
            family_history: medicalBackground["Family History"]?.new || [],
            drug_history: medicalBackground["Drug History"]?.new || [],
            allergies: medicalBackground["Allergies"]?.new || [],
            addiction_history: medicalBackground["Addiction History"]?.new || [],
            menstrual_history: medicalBackground["Menstrual History"]?.new || [],
            surgeries_history: medicalBackground["Surgeries"]?.new || [],
            vaccination_history: medicalBackground["Adult Vaccination"]?.new || [],
        };

        try {
            // htlifeAxios handles auth headers + token refresh automatically.
            await htlifeAxios.post("submit_patient_history/", {
                patient_username: patientUsername || "",
                patient_password: patientPassword || "",
                ...historyPayload,
            });
        } catch (_) {
            // Non-blocking — history save failure should not block the booking step.
        }

        setIsSavingHistory(false);
        proceedToFinal();
    };

    const handleSkipHistory = () => proceedToFinal();

    const proceedToFinal = () => {
        setStep(2);
    };

    const handleClose = () => {
        setStep(0);
        setAppointmentMode("OFFLINE");
        setNotes("");
        setMedicalBackground(initalMedicalBackgroundData);
        setBookingDetails(null);
        onClose();
    };

    const convertToDateTime = (dateStr, time12h) => {
        if (!dateStr || !time12h) return null;
        const [time, modifier] = time12h.trim().split(/\s*(AM|PM)/i);
        let [hours, minutes] = time.split(":");
        hours = parseInt(hours, 10);
        if (modifier?.toUpperCase() === "PM" && hours !== 12) hours += 12;
        else if (modifier?.toUpperCase() === "AM" && hours === 12) hours = 0;
        const date = new Date(dateStr);
        date.setHours(hours);
        date.setMinutes(parseInt(minutes || "00", 10));
        date.setSeconds(0);
        date.setMilliseconds(0);
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, "0");
        const dd = String(date.getDate()).padStart(2, "0");
        const hh = String(date.getHours()).padStart(2, "0");
        const min = String(date.getMinutes()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
            weekday: "long", year: "numeric", month: "long", day: "numeric",
        });
    };

    return (
        <Dialog
            open={open}
            onClose={step === 0 ? handleClose : undefined}
            disableEscapeKeyDown={step > 0}
            maxWidth={step === 1 ? "md" : "sm"}
            fullWidth
            className="appointment-booking-modal"
        >
            {/* ── Header ── */}
            <DialogTitle>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography variant="h6" fontWeight="bold">
                        {step === 0 && "Confirm Appointment"}
                        {step === 1 && "Medical History"}
                        {step === 2 && "Appointment Scheduled!"}
                    </Typography>
                    {step === 0 && (
                        <IconButton onClick={handleClose} disabled={isBooking} size="small">
                            <CloseIcon />
                        </IconButton>
                    )}
                </Box>
                <Stepper activeStep={step} sx={{ mt: 1.5 }} alternativeLabel>
                    {STEPS.map((label) => (
                        <Step key={label}><StepLabel>{label}</StepLabel></Step>
                    ))}
                </Stepper>
            </DialogTitle>

            <DialogContent dividers>

                {/* ═══════════════════════════════════════════════════════════
                    STEP 0 — Booking Form
                ═══════════════════════════════════════════════════════════ */}
                {step === 0 && (
                    <Box>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Please review the appointment details before confirming.
                        </Alert>

                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Appointment Type
                        </Typography>
                        <ToggleButtonGroup
                            value={appointmentMode}
                            exclusive
                            onChange={handleModeChange}
                            fullWidth
                            size="medium"
                            sx={{ mb: 2 }}
                        >
                            <ToggleButton
                                value="OFFLINE"
                                sx={{ gap: 1, "&.Mui-selected": { backgroundColor: "#e8f5e9", color: "#2e7d32", borderColor: "#2e7d32" } }}
                            >
                                <LocalHospitalIcon fontSize="small" />
                                In-Person (Offline)
                            </ToggleButton>
                            <ToggleButton
                                value="ONLINE"
                                sx={{ gap: 1, "&.Mui-selected": { backgroundColor: "#e3f2fd", color: "#1565c0", borderColor: "#1565c0" } }}
                            >
                                <VideoCallIcon fontSize="small" />
                                Telemedicine (Online)
                            </ToggleButton>
                        </ToggleButtonGroup>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">Doctor</Typography>
                            <Typography variant="body1" fontWeight="medium">
                                {doctor.salutation ? `${doctor.salutation} ` : ""}{doctor.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">{doctor.specialization}</Typography>
                        </Box>
                        <Divider sx={{ my: 1.5 }} />
                        <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">Date</Typography>
                            <Typography variant="body1" fontWeight="medium">{formatDate(doctor.next_available_date)}</Typography>
                        </Box>
                        <Divider sx={{ my: 1.5 }} />
                        <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">Time Slot</Typography>
                            <Typography variant="body1" fontWeight="medium">{slot.slot_start_time} – {slot.slot_end_time}</Typography>
                        </Box>
                        <Divider sx={{ my: 1.5 }} />
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">Consultation Fee</Typography>
                            <Typography variant="body1" fontWeight="medium">₹{doctor.consultation_fees}</Typography>
                        </Box>

                        <TextField
                            fullWidth multiline rows={2}
                            label="Additional Notes (Optional)"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Any additional information for the doctor..."
                            disabled={isBooking}
                        />
                    </Box>
                )}

                {/* ═══════════════════════════════════════════════════════════
                    STEP 1 — Medical History (exact Case Sheet History tab)
                ═══════════════════════════════════════════════════════════ */}
                {step === 1 && (
                    <Box>
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Appointment booked with <strong>{bookingDetails?.doctorName}</strong> on{" "}
                            <strong>{formatDate(bookingDetails?.date)}</strong> at{" "}
                            <strong>{bookingDetails?.startTime} – {bookingDetails?.endTime}</strong>.
                        </Alert>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Please fill your medical history so the doctor is prepared for your consultation.
                        </Alert>

                        <HistoryTab
                            medicalBackground={medicalBackground}
                            updateMedicalBackground={updateMedicalBackground}
                            gender=""
                        />
                    </Box>
                )}

                {/* ═══════════════════════════════════════════════════════════
                    STEP 2 — Final Screen
                ═══════════════════════════════════════════════════════════ */}
                {step === 2 && (
                    <Box sx={{ textAlign: "center", py: 3 }}>
                        <Box sx={{
                            width: 90, height: 90, borderRadius: "50%",
                            backgroundColor: "#e3f2fd", display: "flex",
                            alignItems: "center", justifyContent: "center",
                            mx: "auto", mb: 2,
                        }}>
                            <EventAvailableIcon sx={{ fontSize: 48, color: "#1565c0" }} />
                        </Box>
                        <Typography variant="h5" fontWeight="bold" color="#1565c0" gutterBottom>
                            Appointment Scheduled!
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            Your appointment has been added to My Appointments.
                        </Typography>
                        <Box sx={{
                            backgroundColor: "#f9fbe7", border: "1px solid #c5e1a5",
                            borderRadius: 3, p: 3, mb: 3, textAlign: "left",
                        }}>
                            <Typography variant="subtitle2" color="text.secondary">Doctor</Typography>
                            <Typography variant="body1" fontWeight="bold" gutterBottom>{bookingDetails?.doctorName}</Typography>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Date</Typography>
                            <Typography variant="body1" fontWeight="medium" gutterBottom>{formatDate(bookingDetails?.date)}</Typography>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Time</Typography>
                            <Typography variant="body1" fontWeight="medium">{bookingDetails?.startTime} – {bookingDetails?.endTime}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 1 }}>
                            <Button variant="outlined" color="inherit" onClick={handleClose}>
                                Done
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                sx={{ borderRadius: 3, px: 4 }}
                                startIcon={<EventAvailableIcon />}
                                onClick={() => { handleClose(); onGoToMyAppointments?.(); }}
                            >
                                View My Appointments
                            </Button>
                        </Box>
                    </Box>
                )}
            </DialogContent>

            {/* ── Footer Actions ── */}
            {step === 0 && (
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleClose} disabled={isBooking}>Cancel</Button>
                    <Button
                        variant="contained"
                        color={appointmentMode === "ONLINE" ? "primary" : "success"}
                        onClick={handleBookAppointment}
                        disabled={isBooking}
                        startIcon={isBooking ? <CircularProgress size={18} color="inherit" /> : appointmentMode === "ONLINE" ? <VideoCallIcon /> : <LocalHospitalIcon />}
                    >
                        {isBooking ? "Booking..." : appointmentMode === "ONLINE" ? "Book Telemedicine" : "Book In-Person"}
                    </Button>
                </DialogActions>
            )}

            {step === 1 && (
                <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
                    <Button
                        onClick={() => setStep(0)}
                        disabled={isSavingHistory}
                        startIcon={<ArrowBackIcon />}
                        color="inherit"
                    >
                        Back
                    </Button>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button onClick={handleSkipHistory} color="inherit" disabled={isSavingHistory}>
                            Skip
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSaveHistory}
                            disabled={isSavingHistory}
                            startIcon={isSavingHistory ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                        >
                            {isSavingHistory ? "Saving..." : "Submit"}
                        </Button>
                    </Box>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default AppointmentBookingModal;
